import React, { memo } from 'react';
import { useInstantSearch, usePagination } from 'react-instantsearch-hooks-web';
import classNames from 'classnames';
import { DeleteQueryParam } from '../../utils/DeleteQueryParam/DeleteQueryParam.util';
import { SetQueryParam } from '../../utils/SetQueryParam/SetQueryParam';

interface PaginationBarProps {
  pagination_footer_text: string;
  articleContainer?: any;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
  pagination_footer_text,
  articleContainer,
}) => {
  const {
    results: { page, nbHits, hitsPerPage, hits },
  } = useInstantSearch();
  const {
    pages,
    currentRefinement,
    canRefine,
    isFirstPage,
    isLastPage,
    refine,
  } = usePagination();

  const PaginationFooterText = (props: {
    text: string;
    numberOfResultsOnPage: number;
    currentPageIndex: number;
    numberOfHits: number;
  }): JSX.Element => {
    const words: string[] = props.text.split(' ');
    const pageResultsStartNumberPlaceholder: string =
      '<page_results_start_number>';
    const pageResultsEndNumberPlaceholder: string = '<page_results_end_number>';
    const numberOfResultsPlaceholder: string = '<number_of_results>';
    const placeholders: string[] = [
      pageResultsStartNumberPlaceholder,
      pageResultsEndNumberPlaceholder,
      numberOfResultsPlaceholder,
    ];
    return (
      <p className="text-center text-text/body">
        {words.map((word: string) =>
          placeholders.includes(word) ? (
            <span key={word} className="font-bold text-text/body-accent">
              {word === pageResultsStartNumberPlaceholder &&
                `${getIndexOfFirstResultOnPage(
                  props.numberOfResultsOnPage,
                  props.currentPageIndex
                )} `}
              {word === pageResultsEndNumberPlaceholder &&
                `${getIndexOfLastResultOnPage(
                  props.numberOfResultsOnPage,
                  props.currentPageIndex,
                  props.numberOfHits
                )} `}
              {word === numberOfResultsPlaceholder && `${nbHits} `}
            </span>
          ) : (
            `${word} `
          )
        )}
      </p>
    );
  };

  const getIndexOfFirstResultOnPage = (
    numberOfResultsOnPage: number,
    currentPageIndex: number
  ): number => {
    return numberOfResultsOnPage * currentPageIndex + 1;
  };

  const getIndexOfLastResultOnPage = (
    numberOfResultsOnPage: number,
    currentPageIndex: number,
    numberOfHits: number
  ): number => {
    return (
      getIndexOfFirstResultOnPage(numberOfResultsOnPage, currentPageIndex) -
      1 +
      numberOfHits
    );
  };

  const updatePage = (
    event: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
    newPage: number
  ): void => {
    if (canRefine) {
      event.preventDefault();
      refine(newPage);
      newPage === 0
        ? DeleteQueryParam('page')
        : SetQueryParam('page', `${newPage + 1}`);
    }
  };

  return (
    <div className="w-full flex justify-center">
      <div className="space-y-4 w-full flex flex-col items-center justify-center">
        <ul className="flex items-center justify-center bg-White border border-border/light rounded-2xl h-12">
          <li
            key="prev"
            className={classNames(
              'w-12 h-full flex items-center justify-center text-Primary-900 border-r border-r-border/light',
              {
                'hover:underline': !isFirstPage,
                'opacity-30 pointer-events-none': isFirstPage,
              }
            )}
          >
            <button
              className="w-full h-full"
              onClick={(
                event: React.MouseEvent<
                  HTMLButtonElement,
                  globalThis.MouseEvent
                >
              ) => {
                updatePage(event, currentRefinement - 1);
                articleContainer.current.scrollIntoView();
              }}
            >
              {'<'}
            </button>
          </li>
          {pages.map((page: number) => (
            <li
              key={page}
              className={classNames(
                'w-12 h-full flex items-center justify-center border-r border-r-border/light',
                {
                  'text-Primary-900 hover:underline':
                    page !== currentRefinement,
                  'bg-text/body-accent text-system/white hover:no-underline':
                    page === currentRefinement,
                }
              )}
            >
              <button
                className="w-full h-full"
                onClick={(
                  event: React.MouseEvent<
                    HTMLButtonElement,
                    globalThis.MouseEvent
                  >
                ) => {
                  updatePage(event, page);
                  articleContainer.current.scrollIntoView();
                }}
              >
                {page + 1}
              </button>
            </li>
          ))}
          <li
            key="next"
            className={classNames(
              'w-12 h-full flex items-center justify-center text-Primary-900 hover:underline',
              {
                'hover:underline': !isLastPage,
                'opacity-30 pointer-events-none': isLastPage,
              }
            )}
          >
            <button
              className="w-full h-full"
              onClick={(
                event: React.MouseEvent<
                  HTMLButtonElement,
                  globalThis.MouseEvent
                >
              ) => {
                updatePage(event, currentRefinement + 1);
                articleContainer.current.scrollIntoView();
              }}
            >
              {'>'}
            </button>
          </li>
        </ul>
        {pagination_footer_text && (
          <PaginationFooterText
            text={pagination_footer_text}
            numberOfResultsOnPage={hitsPerPage}
            currentPageIndex={page}
            numberOfHits={hits.length}
          />
        )}
      </div>
    </div>
  );
};

export default memo(PaginationBar);
